import { useCloudSettingsQuery, useGridFilters } from "@repo/baseCloud";
import { RepsDropdown, SegmentFilters } from "@repo/baseCloud/components";
import { enumToOptions } from "@repo/helpers";
import {
	Checkbox,
	DateTimeRangePicker,
	DropDownList,
	filterSpanTwoClassName,
	FiltersV2,
	Loading,
	TextBox,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import type { EnrollmentValidationFilter } from "./types";

import EnrollmentValidationApi from "../../api/enrollmentValidationApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { yesNoOptions } from "../../constants/constants";
import { RepIncidentAssignedStatus, RepIncidentStatus } from "../../models";
import { EnrollmentValidationGrid } from "./grid";
import * as STRINGS from "./strings";

const initialFilters: EnrollmentValidationFilter = {
	currentPage: 1,
	itemsPerPage: 10,
	startDate: startOfDay(new Date()),
	endDate: endOfDay(new Date()),
	assignedStatus: "",
	qaAnalystId: null,
	validationTypeIds: [],
	status: "",
	isValid: "",
	enrollmentId: "",
	saleId: "",
	uan: "",
	customerPhone: "",
	customerName: "",
	hasCustomerIncident: undefined,
	branchIds: [],
	repIds: [],
};

export function EnrollmentValidationPage() {
	const {
		filters: draftFilters,
		setFilters: setDraftFilters,
		applyNow,
		appliedFilters,
		setAppliedFilters,
		filtersProps,
	} = useGridFilters(initialFilters);
	// Only narrows the Representatives dropdown, so it stays out of the grid filters.
	const [activeRepsOnly, setActiveRepsOnly] = useState(true);

	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Users],
		queryFn: () => EnrollmentValidationApi.getUsers(),
	});

	const validationTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Types],
		queryFn: () => EnrollmentValidationApi.getTypes(),
	});

	const { settingsLoading } = useCloudSettingsQuery();

	const filterChanged = (value: any, name: string) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	// RepsDropdown narrows by the ISegment keys (brandIds…), not the server parameter names the grid filters use.
	const repSegment = useMemo(
		() => ({
			brandIds: draftFilters.brands,
			channelIds: draftFilters.channels,
			marketerIds: draftFilters.marketers,
			stateIds: draftFilters.states,
			branchIds: draftFilters.branches,
		}),
		[
			draftFilters.brands,
			draftFilters.channels,
			draftFilters.marketers,
			draftFilters.states,
			draftFilters.branches,
		],
	);

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		// The grid keeps a column's first visibility, so it waits for canReview before mounting.
		<Loading loading={settingsLoading}>
			<main>
				<FiltersV2 {...filtersProps}>
					<DateTimeRangePicker
						dateOnly
						id={STRINGS.INCIDENT_DATE}
						label="Date of Error"
						className={filterSpanTwoClassName}
						quickRange
						quickRangeOptions={["today", "yesterday", "month", "year", "all"]}
						value={[draftFilters.startDate, draftFilters.endDate]}
						setValue={(v: (Date | undefined)[]) =>
							setDraftFilters({ ...draftFilters, startDate: v[0], endDate: v[1] })
						}
						onQuickRangeSelected={(v) => applyNow((prev) => ({ ...prev, startDate: v[0], endDate: v[1] }))}
					/>

					<SegmentFilters onChange={segmentFiltersChanged} />
					<RepsDropdown
						id={STRINGS.REP_IDS}
						label="Representatives"
						value={draftFilters.repIds}
						changeHandler={filterChanged}
						filters={repSegment}
						onlyActiveReps={activeRepsOnly}
					/>
					<Checkbox
						id={STRINGS.ACTIVE_REPS_ONLY}
						title="Active Reps Only"
						label=""
						value={activeRepsOnly}
						changeHandler={(checked: boolean) => setActiveRepsOnly(checked)}
						inline={false}
					/>

					<DropDownList
						id={STRINGS.ASSIGNED_STATUS}
						name={STRINGS.ASSIGNED_STATUS}
						label="Assigned Status"
						value={draftFilters.assignedStatus}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={enumToOptions(RepIncidentAssignedStatus)}
					/>
					<DropDownList
						id={STRINGS.QA_ANALYST_ID}
						name={STRINGS.QA_ANALYST_ID}
						label="QA Analyst"
						value={draftFilters.qaAnalystId}
						changeHandler={filterChanged}
						values={usersQuery.data ?? []}
						loaded={usersQuery.isFetched}
						clearable
						fullLength
						valueProp="Id"
						textProp="Name"
					/>
					<DropDownList
						id={STRINGS.VALIDATION_TYPE_IDS}
						name={STRINGS.VALIDATION_TYPE_IDS}
						label="Validation Type"
						value={draftFilters.validationTypeIds}
						changeHandler={filterChanged}
						fullLength
						clearable
						loaded={validationTypesQuery.isFetched}
						values={validationTypesQuery.data ?? []}
						isMulti
						textProp="Name"
						valueProp="Id"
					/>

					<DropDownList
						id={STRINGS.STATUS}
						name={STRINGS.STATUS}
						label="Status"
						value={draftFilters.status}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={enumToOptions(RepIncidentStatus)}
					/>

					<DropDownList
						id={STRINGS.IS_VALID}
						name={STRINGS.IS_VALID}
						label="Is Valid?"
						value={draftFilters.isValid}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={yesNoOptions}
					/>

					<TextBox
						id={STRINGS.ENROLLMENT_ID}
						name={STRINGS.ENROLLMENT_ID}
						label="Enrollment Id"
						value={draftFilters.enrollmentId}
						changeHandler={filterChanged}
					/>

					<TextBox
						id={STRINGS.SALE_ID}
						name={STRINGS.SALE_ID}
						label="Sale Id"
						value={draftFilters.saleId}
						changeHandler={filterChanged}
					/>

					<TextBox
						id={STRINGS.ACCOUNT_NUMBER}
						name={STRINGS.ACCOUNT_NUMBER}
						label="Account Number"
						value={draftFilters.uan}
						changeHandler={filterChanged}
					/>

					<TextBox
						id={STRINGS.CUSTOMER_PHONE}
						name={STRINGS.CUSTOMER_PHONE}
						label="Customer Phone #"
						value={draftFilters.customerPhone}
						changeHandler={filterChanged}
					/>

					<TextBox
						id={STRINGS.CUSTOMER_NAME}
						name={STRINGS.CUSTOMER_NAME}
						label="Customer Name"
						value={draftFilters.customerName}
						changeHandler={filterChanged}
					/>

					<DropDownList
						id={STRINGS.HAS_CUSTOMER_INCIDENT}
						name={STRINGS.HAS_CUSTOMER_INCIDENT}
						label="Has Customer Incident"
						value={draftFilters.hasCustomerIncident}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={yesNoOptions}
					/>
				</FiltersV2>
				<EnrollmentValidationGrid filters={appliedFilters} setFilters={setAppliedFilters} />
			</main>
		</Loading>
	);
}
